<template>
    <vx-table-row>
        <div
            class="rounded-t-2xl col-span-full flex flex-wrap items-center gap-y-1 gap-x-2 -mx-6 -mt-6 mb-2 p-4 bg-slate-50 border-b border-solid border-slate-100 sm:px-6 sm:py-4 xl:hidden"
            data-test="mobile-header"
        >
            <div
                class="cursor-pointer flex items-center order-2 sm:order-1"
                data-test="toggle-favorite-mobile"
                @click="toggleFavorite()"
            >
                <font-awesome-icon
                    widthAuto
                    v-if="group.is_favorite"
                    :icon="faStar"
                    class="text-amber-400"
                ></font-awesome-icon>
                <font-awesome-icon widthAuto v-else :icon="faStarReg" class="text-slate-500"></font-awesome-icon>
            </div>
            <div class="font-bold text-slate-700 text-sm flex-1 min-w-0 truncate order-1 sm:order-2">
                {{ group.name }}
            </div>
            <div class="basis-full h-0 order-3 sm:hidden" aria-hidden="true"></div>
            <group-status-badge v-if="group.status" :status="group.status" class="order-4" />
        </div>

        <vx-table-row-title class="max-xl:hidden">
            <div class="flex items-center gap-3">
                <div class="cursor-pointer" data-test="toggle-favorite" @click="toggleFavorite()">
                    <font-awesome-icon
                        widthAuto
                        v-if="group.is_favorite"
                        :icon="faStar"
                        class="text-amber-400"
                    ></font-awesome-icon>
                    <font-awesome-icon widthAuto v-else :icon="faStarReg" class="text-slate-500"></font-awesome-icon>
                </div>
                <div class="text-xs sm:text-sm wrap-break-word max-w-[200px]">
                    {{ group.name }}
                </div>
            </div>
        </vx-table-row-title>

        <vx-table-row-item data-test="external-id">
            <template v-slot:label> External ID: </template>
            <span class="break-all text-xs">{{ group.group_id }}</span>
        </vx-table-row-item>

        <vx-table-row-item data-test="team">
            <template v-slot:label> Team: </template>
            <kiosk-team-cell
                :teamId="group.team_id"
                :teamName="group.team_name"
                :teamStatus="group.team_status"
                :teamStatusReason="group.team_status_reason"
            />
        </vx-table-row-item>

        <vx-table-row-item data-test="org-unit">
            <template v-slot:label> OU: </template>
            <vx-popover v-if="group.org_unit_name" hover :arrow="true" placement="top">
                <span class="inline-flex items-center gap-1">
                    <font-awesome-icon widthAuto :icon="faBuildingFlag"></font-awesome-icon>
                    <span class="text-xs font-normal text-slate-700 xl:hidden" data-test="org-unit-name">
                        {{ group.org_unit_name }}
                    </span>
                </span>
                <template v-slot:content>
                    <span class="text-sm font-normal">{{ group.org_unit_name }}</span>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item data-test="status" class="max-xl:hidden">
            <template v-slot:label> Status: </template>
            <div class="flex gap-1 flex-col items-start">
                <group-status-badge v-if="group.status" :status="group.status" />
            </div>
        </vx-table-row-item>

        <vx-table-row-item data-test="billable">
            <template v-slot:label> Billable: </template>
            <vx-badge :color="group.is_billable ? 'success' : 'slate'" size="xs">
                {{ group.is_billable ? 'Yes' : 'No' }}
            </vx-badge>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-dropdown placement="right">
                <vx-button data-test="dropdown" size="sm" type="button" color="muted" outline class="w-9">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item :href="`/kiosk/teams/${group.team_id}/groups/${group.group_id}`">
                        View
                        <font-awesome-icon widthAuto :icon="faEye"></font-awesome-icon>
                    </vx-dropdown-item>

                    <vx-dropdown-item data-test="change-status" @click="statusModalVisible = true">
                        Change Status
                        <font-awesome-icon widthAuto :icon="faArrowsRotate"></font-awesome-icon>
                    </vx-dropdown-item>

                    <vx-dropdown-item v-if="group.is_favorite" @click="toggleFavorite()">
                        Remove Favorite
                        <font-awesome-icon widthAuto :icon="faStar"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-else @click="toggleFavorite()">
                        Favorite
                        <font-awesome-icon widthAuto :icon="faStarReg"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <update-group-status-modal
                v-model:visible="statusModalVisible"
                :teamId="group.team_id"
                :groupId="group.group_id"
                :groupName="group.name"
                :status="group.status"
                :isBillable="group.is_billable"
                @updated="onStatusUpdated"
            />

            <vx-popover hover placement="top" arrow>
                <vx-button
                    :href="`/kiosk/users/${group.team_owner_id}/impersonate?team_id=${group.team_id}&group_id=${group.group_id}`"
                    :disabled="impersonateDisabled"
                    size="sm"
                    data-test="impersonate"
                    outline
                    color="muted"
                    class="w-full xl:w-9"
                    aria-label="Impersonate"
                >
                    <span class="xl:hidden">Impersonate</span>
                    <font-awesome-icon widthAuto :icon="faMasksTheater" />
                </vx-button>
                <template v-slot:content>
                    <span>Impersonate</span>
                </template>
            </vx-popover>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import {
    faArrowsRotate,
    faBuildingFlag,
    faEllipsis,
    faEye,
    faStar,
    faMasksTheater,
} from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarReg } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { adminGroupsClient } from '~/services';
import { computed, ref } from 'vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';
import KioskTeamCell from '~/components/kiosk/KioskTeamCell.vue';
import UpdateGroupStatusModal from '~/components/kiosk/kiosk-groups/list/UpdateGroupStatusModal.vue';

const group = defineModel({
    required: true,
    type: Object,
});

const currentUserId = Spark.state.user.id;

const impersonateDisabled = computed(
    () => group.value.team_owner_id === currentUserId || group.value.team_status === 'closed'
);

const toasted = useToasted();

const statusModalVisible = ref(false);

const onStatusUpdated = (status) => {
    group.value.status = status;
};

const togglingFavorite = ref(false);
const toggleFavorite = async () => {
    if (togglingFavorite.value) {
        return;
    }
    togglingFavorite.value = true;
    try {
        if (group.value.is_favorite) {
            await adminGroupsClient.deleteFavoriteGroup(group.value.team_id, group.value.group_id);
            group.value.is_favorite = false;
            toasted.success(
                `${group.value.group_id} - ${group.value.name} has been removed from your favorite groups.`
            );
        } else {
            await adminGroupsClient.favoriteGroup(group.value.team_id, group.value.group_id);
            group.value.is_favorite = true;
            toasted.success(`${group.value.group_id} - ${group.value.name} has been added to your favorite groups.`);
        }
    } catch (e) {
        console.error(e);
        if (e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        toasted.error(e.response?.data?.message || 'Something went wrong.');
    } finally {
        togglingFavorite.value = false;
    }
};
</script>
